import React, { useState } from 'react';
import { Button, Input, Textarea } from '@material-tailwind/react';
import { SnowflakeIcon } from 'lucide-react';
import Modal from '../../../components/modal-x';
import { useFreezeBooking } from '../../../custom-hooks/use-bookings';
import moment from 'moment';

const BookingFreezeModal = ({ isOpen, onClose, booking, onSuccess }) => {
    const bookingEnd = booking?.endDate ? moment(booking.endDate).format('YYYY-MM-DD') : '';

    const [freezeStartDate, setFreezeStartDate] = useState('');
    const [freezeEndDate, setFreezeEndDate] = useState('');
    const [reason, setReason] = useState('');
    const [error, setError] = useState(null);

    const freezeMutation = useFreezeBooking();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!freezeStartDate || !freezeEndDate || !reason) {
            setError('All fields are required.');
            return;
        }

        if (moment(freezeStartDate).isSameOrAfter(moment(freezeEndDate), 'day')) {
            setError('Freeze start date must be before the end date.');
            return;
        }

        if (bookingEnd && moment(freezeEndDate).isAfter(moment(bookingEnd), 'day')) {
            setError('Freeze end date cannot be after the booking end date.');
            return;
        }

        try {
            await freezeMutation.mutateAsync({
                bookingId: booking._id,
                data: { freezeStartDate, freezeEndDate, reason },
            });
            onSuccess?.();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to freeze booking.');
        }
    };

    return (
        <Modal title="Freeze Booking" isOpen={isOpen} onClose={onClose}>
            <form onSubmit={handleSubmit}>
                <div className="flex flex-col gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                        <p className="font-semibold mb-1">What happens when you freeze a booking?</p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                            <li>Revenue for this booking will not be counted during the freeze period</li>
                            <li>The seat / office remains reserved for this customer</li>
                            <li>The space can be temporarily booked by another customer within the freeze window</li>
                            <li>Manual unfreeze is required to resume this booking after the freeze period ends</li>
                        </ul>
                    </div>
 
                    <Input
                        label="Freeze Start Date"
                        type="date"
                        required
                        value={freezeStartDate}
                        max={bookingEnd || undefined}
                        onChange={(e) => setFreezeStartDate(e.target.value)}
                    />
                    <Input
                        label="Freeze End Date"
                        type="date"
                        required
                        value={freezeEndDate}
                        max={bookingEnd || undefined}
                        onChange={(e) => setFreezeEndDate(e.target.value)}
                    />
                    <Textarea
                        label="Reason for Freeze"
                        required
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />

                    {error && <p className="text-red-500 text-sm">{error}</p>}

                    <Button
                        className="w-full rounded-full flex items-center justify-center gap-2"
                        variant="filled"
                        color="blue"
                        type="submit"
                        disabled={freezeMutation.isLoading}
                        loading={freezeMutation.isLoading}
                    >
                        <SnowflakeIcon className="w-4 h-4" />
                        Freeze Booking
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default BookingFreezeModal;
